import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/index'

Vue.use(Router)

export default new Router({
  mode: 'history',
  base: process.env.BASE_URL,

  /*
   * | ------------------------
   * | Simulate native-like scroll behavior when navigating to a new
   * | route and using back/forward buttons.
   * | ------------------------
   * |
   */
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },

  /*
   * | ------------------------
   * | All router page routes
   * | Contains all routes and their matching components
   * | ------------------------
   * |
   */
  routes: [
    {
      path: '/',
      name: 'main',
      beforeEnter: guardMulti
    },
    {
      path: '/login',
      name: 'login',
      beforeEnter: guardGuest,
      component: () => lazyLoadView(import('@/views/auth/login.vue'))
    },
    {
      path: '/register',
      name: 'register',
      beforeEnter: guardGuest,
      component: () => lazyLoadView(import('@/views/auth/register.vue'))
    },
    {
      path: '/reset-password',
      name: 'resetPassword',
      beforeEnter: guardGuest,
      component: () => lazyLoadView(import('@/views/auth/reset-password.vue'))
    },
    {
      path: '/',
      beforeEnter: guardAuth,
      component: () => lazyLoadView(import('@/views/index.vue')),
      children: [
        {
          path: 'shipments',
          name: 'shipments',
          component: () => lazyLoadView(import('@/views/shipments/index.vue'))
        },
        {
          path: 'shipments/:shipmentId',
          name: 'shipment-detail',
          component: () => lazyLoadView(import('@/views/shipments/detail.vue')),
          meta: { type: 'shipment' }
        },
        {
          path: 'search',
          name: 'search',
          component: () => lazyLoadView(import('@/views/search/index.vue')),
          meta: {
            hideFooter: true
          }
        },
        {
          path: 'search/:shipmentId',
          name: 'search-detail',
          component: () => lazyLoadView(import('@/views/shipments/detail.vue')),
          meta: { type: 'search' }
        },
        {
          path: 'offers',
          name: 'offers',
          component: () => lazyLoadView(import('@/views/offers/index.vue'))
        },
        {
          path: 'offers/:shipmentId',
          name: 'offer-detail',
          component: () => lazyLoadView(import('@/views/shipments/detail.vue')),
          meta: { type: 'offer' }
        },
        {
          path: 'fleet',
          name: 'fleet',
          component: () => lazyLoadView(import('@/views/fleet/index.vue'))
        },
        {
          path: 'chat',
          name: 'chat',
          component: () => lazyLoadView(import('@/views/chat/index.vue')),
          meta: {
            hideFooter: true
          }
        },
        {
          path: 'chat/:shipmentId',
          name: 'chat-detail',
          component: () => lazyLoadView(import('@/views/chat/index.vue')),
          meta: {
            hideFooter: true
          }
        },
        {
          path: 'profile/',
          component: () => lazyLoadView(import('@/views/user/index.vue')),
          children: [
            {
              path: 'settings',
              name: 'profile-settings',
              component: () => lazyLoadView(import('@/views/user/settings.vue'))
            },
            {
              path: 'company',
              name: 'profile-company',
              component: () => lazyLoadView(import('@/views/user/company.vue'))
            },
            {
              path: 'payment',
              name: 'profile-payment',
              component: () => lazyLoadView(import('@/views/user/payment.vue'))
            },
            {
              path: 'notification',
              name: 'profile-notifications',
              component: () => lazyLoadView(import('@/views/user/notifications.vue'))
            }
          ]
        }
      ]
    }
  ]
})

/**
 * Guard auth  and guest middleware
 * Redirect to the login page when the user is not logged in
 * Redirect to the home page when the user is logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardMulti(to, from, next) {
  store.getters.isLoggedIn ? next({ name: 'search' }) : next({ name: 'login' })
}

/**
 * Guard guest middleware
 * Redirect to the home page when the user is logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardGuest(to, from, next) {
  !store.getters.isLoggedIn ? next() : next({ name: 'search' })
}

/**
 * Guard auth middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardAuth(to, from, next) {
  if (!store.getters.user) store.dispatch('getAuthUser')
  store.getters.isLoggedIn ? next() : next({ name: 'login' })
}

/**
 * Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * @param {AsyncView} The view component
 */
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    delay: 400,
    timeout: 10000
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      return h(AsyncHandler, data, children)
    }
  })
}
